<header class="flex items-center gap-3 px-4 pb-4 pt-2">
  <p-button
    icon="pi pi-arrow-left"
    (onClick)="back()"
    [text]="true"
    [rounded]="true"
    pTooltip="Back to list"
  />
  <div>
    <h1 class="text-2xl font-semibold text-greyscale-900">
      Document analysis job
    </h1>
    <p class="text-sm text-greyscale-600">AI brochure extraction job detail</p>
  </div>
</header>

@if (loading()) {
  <div class="p-8 text-center text-gray-500">
    <i class="pi pi-spin pi-spinner text-2xl"></i>
    <p class="mt-2">Loading…</p>
  </div>
} @else if (job()) {
  @let j = job()!;
  <main class="space-y-4 p-4">
    <!-- Header card: filename + status + type + open brochure -->
    <p-card>
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="text-xs uppercase text-gray-500">File</div>
          <div class="break-all text-lg font-semibold">
            {{ j.originalFileName }}
          </div>
          <div class="mt-1 break-all text-xs text-gray-400">{{ j.s3Key }}</div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <span
            class="whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium"
            [ngClass]="getStatusClasses(j.status)"
          >
            {{ j.status }}
          </span>
          <span
            class="whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium"
            [ngClass]="
              jobType() === DocumentJobType.SINGLE
                ? 'bg-primary-100 text-primary-700'
                : 'bg-greyscale-100 text-greyscale-700'
            "
          >
            {{ jobType() === DocumentJobType.SINGLE ? "Single" : "Batch" }}
          </span>
          <p-button
            icon="pi pi-external-link"
            label="Open brochure"
            (onClick)="openBrochure()"
            [disabled]="!j.brochureUrl"
            pTooltip="Opens a signed S3 URL (expires in 1h)"
          />
        </div>
      </div>
    </p-card>

    <!-- User & company -->
    <p-card>
      <ng-template #title>User & company</ng-template>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div class="text-xs uppercase text-gray-500">User</div>
          <div class="font-medium">
            {{ j.user.firstName }} {{ j.user.lastName }}
          </div>
          <div class="text-xs text-gray-400">{{ j.user.id }}</div>
        </div>
        <div>
          <div class="text-xs uppercase text-gray-500">Company</div>
          <div class="font-medium">{{ j.company.name }}</div>
          <div class="text-xs text-gray-400">{{ j.company.id }}</div>
        </div>
      </div>
    </p-card>

    <!-- Timeline -->
    <p-card>
      <ng-template #title>Timeline</ng-template>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <div class="text-xs uppercase text-gray-500">Created at</div>
          <div class="font-medium">{{ formatDate(j.createdAt) }}</div>
        </div>
        <div>
          <div class="text-xs uppercase text-gray-500">Processed at</div>
          <div class="font-medium">{{ formatDate(j.processedAt) }}</div>
        </div>
        <div>
          <div class="text-xs uppercase text-gray-500">Duration</div>
          <div class="font-medium">{{ formatDuration(durationSeconds()) }}</div>
        </div>
      </div>
      <div class="mt-4 text-xs text-gray-500">Batch ID: {{ j.batchId }}</div>
    </p-card>

    <!-- Linked trip -->
    <p-card>
      <ng-template #title>Linked trip</ng-template>
      @if (j.trip) {
        <div class="flex items-center justify-between gap-4">
          <div class="min-w-0">
            <div class="truncate font-medium">{{ j.trip.title }}</div>
            <div class="text-xs text-gray-500">Status: {{ j.trip.status }}</div>
          </div>
          <a
            [routerLink]="['/home/trips', j.trip.id, 'dashboard']"
            class="text-primary-700 hover:underline"
          >
            Open trip <i class="pi pi-external-link ml-1"></i>
          </a>
        </div>
      } @else {
        <div class="text-sm text-gray-500">
          No trip was created for this job.
        </div>
      }
    </p-card>

    <!-- Metadata (success or error) -->
    @if (j.status === DocumentJobStatus.COMPLETED && successMetadata(); as sm) {
      <p-card>
        <ng-template #title>Extraction metadata</ng-template>
        <dl class="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
          @if (sm.confidenceScore !== undefined) {
            <div>
              <dt class="text-xs uppercase text-gray-500">Confidence score</dt>
              <dd class="font-medium">{{ sm.confidenceScore }}</dd>
            </div>
          }
          @if (sm.extractionMethod) {
            <div>
              <dt class="text-xs uppercase text-gray-500">Extraction method</dt>
              <dd class="font-medium">{{ sm.extractionMethod }}</dd>
            </div>
          }
          @if (sm.warnings && sm.warnings.length) {
            <div class="md:col-span-2">
              <dt class="text-xs uppercase text-gray-500">Warnings</dt>
              <ul class="mt-1 list-disc pl-5">
                @for (w of sm.warnings; track w) {
                  <li>{{ w }}</li>
                }
              </ul>
            </div>
          }
        </dl>
      </p-card>
    }

    @if (j.status === DocumentJobStatus.FAILED) {
      <p-card>
        <ng-template #title>Failure details</ng-template>

        @if (errorMetadata(); as em) {
          @if (em.errorCode) {
            <div class="mb-3">
              <div class="text-xs uppercase text-gray-500">Error code</div>
              <div class="font-mono font-medium text-error-700">
                {{ em.errorCode }}
              </div>
            </div>
          }
          @if (em.failedAt) {
            <div class="mb-3">
              <div class="text-xs uppercase text-gray-500">Failed at</div>
              <div class="font-medium">{{ formatDate(em.failedAt) }}</div>
            </div>
          }
          @if (errorContextJson(); as ctx) {
            <div class="mb-3">
              <div class="mb-1 text-xs uppercase text-gray-500">
                Error context
              </div>
              <pre
                class="overflow-x-auto rounded border bg-greyscale-50 p-3 text-xs"
                >{{ ctx }}</pre
              >
            </div>
          }
        } @else {
          <div class="mb-3 text-sm text-gray-500">
            No structured metadata — likely a post-IA failure (normalize /
            createTrip / Google Maps / email).
          </div>
        }

        @if (j.errorMessage) {
          <div>
            <div class="text-xs uppercase text-gray-500">Error message</div>
            <div class="break-words font-medium">{{ j.errorMessage }}</div>
          </div>
        }
      </p-card>
    }
  </main>
} @else {
  <div class="p-8 text-center text-gray-500">Job not found.</div>
}

<p-toast />
